import React, { useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, BookOpen, FileText, Mail, MessageCircle } from "lucide-react";

const TABS = [
  { key: "homework_hint", label: "Homework Hint", icon: BookOpen, fields: [["subject", "Subject"], ["topic", "Topic"], ["grade", "Grade"]] },
  { key: "exam_questions", label: "Exam Questions", icon: FileText, fields: [["subject", "Subject"], ["topic", "Topic"], ["grade", "Grade"]] },
  { key: "report_comment", label: "Report Comment", icon: MessageCircle, fields: [["student_name", "Student name"], ["subject", "Subject"], ["marks", "Marks (%)"], ["grade", "Grade"], ["context", "Context"]] },
  { key: "parent_message", label: "Parent Message", icon: Mail, fields: [["student_name", "Student name"], ["context", "Context"]] },
];

export default function AIAssistantPage() {
  const [tab, setTab] = useState(TABS[0].key);
  const [state, setState] = useState({});
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const active = TABS.find((t) => t.key === tab);

  const set = (k, v) => setState({ ...state, [k]: v });

  const run = async () => {
    setLoading(true);
    setResult("");
    try {
      const { data } = await api.post("/ai/generate", { kind: tab, ...state });
      setResult(data.text + (data.fallback ? "\n\n(⚠ AI service fell back to template)" : ""));
    } catch (e) {
      setResult("Error: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="ai-page">
      <div>
        <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Assistant</div>
        <h1 className="text-4xl font-bold tracking-tight flex items-center gap-3"><Sparkles className="h-8 w-8 text-primary" /> AI Assistant</h1>
        <p className="text-muted-foreground text-sm mt-1">Powered by GPT-5.2 · Draft homework hints, exam questions, report comments and parent messages.</p>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setResult(""); setState({}); }}>
        <TabsList className="rounded-2xl grid grid-cols-2 md:grid-cols-4 h-auto p-1">
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} className="rounded-xl gap-2 py-3" data-testid={`ai-tab-${t.key}`}>
              <t.icon className="h-4 w-4" /> <span className="hidden md:inline">{t.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-4">
            <div className="grid gap-4 lg:grid-cols-2">
              <Card className="rounded-2xl">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><t.icon className="h-4 w-4 text-primary" /> {t.label}</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {t.fields.map(([k, label]) => (
                    <div key={k}>
                      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
                      {k === "context" ? (
                        <Textarea value={state[k] || ""} onChange={(e) => set(k, e.target.value)} className="rounded-2xl mt-1.5" data-testid={`ai-${k}`} />
                      ) : (
                        <Input value={state[k] || ""} onChange={(e) => set(k, e.target.value)} className="rounded-2xl mt-1.5" data-testid={`ai-${k}`} />
                      )}
                    </div>
                  ))}
                  <Button onClick={run} disabled={loading} className="rounded-2xl w-full mt-2" data-testid="ai-run">
                    {loading ? "Generating…" : "Generate"}
                  </Button>
                </CardContent>
              </Card>

              <Card className="rounded-2xl">
                <CardHeader><CardTitle className="text-base">Result</CardTitle></CardHeader>
                <CardContent>
                  <div className="min-h-64 p-4 rounded-xl bg-muted/40 whitespace-pre-wrap text-sm leading-relaxed" data-testid="ai-result">
                    {loading ? "Thinking…" : (result || "Fill the form and click Generate.")}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
